import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  BarChart3,
  FolderKanban,
  ListTodo
} from 'lucide-react';

function TrendBadge({ current, previous, label = 'sem. préc.' }) {
  if (previous == null || previous === 0) return null;
  const diff = ((current - previous) / previous * 100);
  const isUp = diff >= 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium mt-1 ${isUp ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? '+' : ''}{diff.toFixed(0)}% vs {label}
    </span>
  );
}
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TimeEntries() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workItems, setWorkItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      // Fetch projects
      const projectsRes = await axios.get(`${API_URL}/api/users/me/projects`, {
        headers: getAuthHeader()
      });
      setProjects(projectsRes.data);

      // Fetch all Azure work items
      const workItemsRes = await axios.get(`${API_URL}/api/work-items/azure`, {
        headers: getAuthHeader()
      });
      setWorkItems(workItemsRes.data.work_items || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }

  // Filter work items by selected project
  const filteredWorkItems = selectedProject === 'all'
    ? workItems
    : workItems.filter(item => item.project_id === selectedProject);

  // Calculate statistics
  const totalHours = filteredWorkItems.reduce((sum, item) => sum + (item.completed_hours || 0), 0);
  const completedTasks = filteredWorkItems.filter(item =>
    ['Done', 'Closed', 'Completed'].includes(item.state)
  ).length;
  const inProgressTasks = filteredWorkItems.filter(item =>
    ['In Progress', 'Active'].includes(item.state)
  ).length;

  // Calculate hours by project
  const projectStats = projects.map(project => {
    const projectItems = workItems.filter(item => item.project_id === project.id);
    const hours = projectItems.reduce((sum, item) => sum + (item.completed_hours || 0), 0);
    const tasksCount = projectItems.length;
    const completedCount = projectItems.filter(item =>
      ['Done', 'Closed', 'Completed'].includes(item.state)
    ).length;
    return {
      ...project,
      hours,
      tasksCount,
      completedCount
    };
  }).filter(p => p.hours > 0 || p.tasksCount > 0);

  // Work items with hours
  const itemsWithHours = filteredWorkItems
    .filter(item => (item.completed_hours || 0) > 0)
    .sort((a, b) => (b.completed_hours || 0) - (a.completed_hours || 0));

  // Compute time-based trends from changed_date
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekDay = todayMidnight.getDay();
  const weekStart = new Date(todayMidnight);
  weekStart.setDate(todayMidnight.getDate() - (weekDay === 0 ? 6 : weekDay - 1));
  const lastWeekStart = new Date(weekStart);
  lastWeekStart.setDate(weekStart.getDate() - 7);
  const thisMonthStart = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), 1);
  const lastMonthStart = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth() - 1, 1);
  const lastMonthEnd = thisMonthStart;

  const thisWeekHours = filteredWorkItems.reduce((sum, item) => {
    if (!item.changed_date) return sum;
    return new Date(item.changed_date) >= weekStart ? sum + (item.completed_hours || 0) : sum;
  }, 0);
  const lastWeekHours = filteredWorkItems.reduce((sum, item) => {
    if (!item.changed_date) return sum;
    const d = new Date(item.changed_date);
    return d >= lastWeekStart && d < weekStart ? sum + (item.completed_hours || 0) : sum;
  }, 0);
  const thisMonthHours = filteredWorkItems.reduce((sum, item) => {
    if (!item.changed_date) return sum;
    return new Date(item.changed_date) >= thisMonthStart ? sum + (item.completed_hours || 0) : sum;
  }, 0);
  const lastMonthHours = filteredWorkItems.reduce((sum, item) => {
    if (!item.changed_date) return sum;
    const d = new Date(item.changed_date);
    return d >= lastMonthStart && d < lastMonthEnd ? sum + (item.completed_hours || 0) : sum;
  }, 0);

  const getWorkItemTypeClass = (type) => {
    const classes = {
      'Epic': 'work-item-epic',
      'Feature': 'work-item-feature',
      'UserStory': 'work-item-userstory',
      'Task': 'work-item-task',
      'Bug': 'work-item-bug'
    };
    return classes[type] || '';
  };

  const getStateClass = (state) => {
    if (['Done', 'Closed', 'Completed'].includes(state)) return 'state-done';
    if (['Active', 'In Progress'].includes(state)) return 'state-active';
    return 'state-new';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-entries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-extrabold">Temps</h1>
          <p className="text-muted-foreground mt-1">
            Visualisez les heures completees par projet et par tache
          </p>
        </div>
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tous les projets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les projets</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Heures cette semaine</p>
              <p className="kpi-value mt-2">{thisWeekHours.toFixed(1)}h</p>
              <TrendBadge current={thisWeekHours} previous={lastWeekHours} />
            </div>
            <div className="p-3 bg-primary/10 rounded-sm">
              <Clock className="h-6 w-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Heures ce mois</p>
              <p className="kpi-value mt-2">{thisMonthHours.toFixed(1)}h</p>
              <TrendBadge current={thisMonthHours} previous={lastMonthHours} label="mois préc." />
            </div>
            <div className="p-3 bg-blue-500/10 rounded-sm">
              <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Tâches Complétées</p>
              <p className="kpi-value mt-2">{completedTasks}</p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-sm">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Total Heures</p>
              <p className="kpi-value mt-2">{totalHours.toFixed(1)}h</p>
            </div>
            <div className="p-3 bg-yellow-500/10 rounded-sm">
              <TrendingUp className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Stats */}
        <Card className="lg:col-span-1">
          <CardHeader className="border-b border-border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-heading">Par Projet</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {projectStats.length > 0 ? (
              <div className="divide-y divide-border">
                {projectStats.map(project => (
                  <div
                    key={project.id}
                    className="p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium truncate">{project.name}</span>
                      <Badge variant="secondary" className="font-mono shrink-0">
                        {project.hours.toFixed(1)}h
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{project.tasksCount} tâche{project.tasksCount > 1 ? 's' : ''}</span>
                      <span>•</span>
                      <span>{project.completedCount} complétée{project.completedCount > 1 ? 's' : ''}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-2">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{
                          width: `${project.tasksCount > 0 ? (project.completedCount / project.tasksCount * 100) : 0}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-12">
                <FolderKanban className="empty-state-icon" />
                <p className="empty-state-title">Aucun projet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Task Details */}
        <Card className="lg:col-span-2">
          <CardHeader className="border-b border-border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-heading">Détails par Tâche</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {itemsWithHours.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Tâche</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">État</TableHead>
                    <TableHead className="font-semibold text-right">Heures</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itemsWithHours.map(item => (
                    <TableRow
                      key={item.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => window.open(item.url, '_blank')}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium truncate max-w-[300px]">
                            {item.title}
                          </span>
                          {item.project_name && (
                            <span className="text-xs text-muted-foreground">
                              {item.project_name}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getWorkItemTypeClass(item.work_item_type)}
                        >
                          {item.work_item_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={getStateClass(item.state)}
                        >
                          {item.state}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {(item.completed_hours || 0).toFixed(1)}h
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="empty-state py-12">
                <Clock className="empty-state-icon" />
                <p className="empty-state-title">Aucune heure complétée</p>
                <p className="empty-state-description">
                  Les heures complétées apparaîtront ici
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default TimeEntries;
